/**
 * Registro global de modais abertos para gerenciamento de stack.
 *
 * Implementado como singleton para coordenar múltiplos modais simultâneos,
 * garantindo que apenas o modal do topo da stack responda ao evento popstate.
 * A stack segue o padrão LIFO (Last In, First Out) com limite máximo de 5 modais.
 *
 * @class ModalStackRegistry
 *
 * @example
 * import { modalStackRegistry } from '../core/registries/modal-stack-registry.js'
 *
 * // Registrar modal ao abrir
 * modalStackRegistry.register(this)
 *
 * // Verificar se é o topo antes de responder a popstate
 * if (modalStackRegistry.isTop(this)) {
 *   this.close()
 * }
 *
 * // Remover modal ao fechar
 * modalStackRegistry.unregister(this)
 */

/** Limite máximo de modais simultâneos na stack */
const MAX_STACK_SIZE = 5

class ModalStackRegistry {
  /**
   * Stack LIFO de modais abertos.
   * O último elemento é o topo (modal mais recente).
   *
   * @type {Array<object>}
   * @private
   */
  _stack = []

  /**
   * Flag que indica se um evento popstate já foi consumido no tick atual.
   *
   * Quando múltiplos modais estão na stack, todos registram listeners de popstate.
   * Sem essa flag, um único evento popstate pode propagar para vários listeners
   * no mesmo tick, fechando múltiplos modais simultaneamente.
   * A flag é setada em true ao consumir o popstate e resetada para false
   * no próximo microtask via queueMicrotask().
   *
   * @type {boolean}
   * @private
   */
  _popstateConsumed = false

  /**
   * Flag que indica se uma limpeza de histórico órfão está em progresso.
   * Quando true, handlers de popstate de outros modais devem ignorar o evento.
   * Setado por _cleanupOrphanHistory() e resetado após 50ms via setTimeout.
   * @type {boolean}
   * @private
   */
  _cleanupInProgress = false

  /**
   * Adiciona um modal ao topo da stack.
   *
   * Se o modal já estiver registrado, a operação é ignorada (sem duplicatas).
   * Se a stack atingir o limite máximo (5 modais), a operação é rejeitada
   * com um warning no console.
   *
   * @param {object} modal - Instância do modal (componente Lit com ModalBaseMixin)
   * @returns {void}
   */
  register (modal) {
    if (!modal) return

    // Evita duplicatas na stack
    if (this._stack.includes(modal)) return

    // Limite de segurança para UX
    if (this._stack.length >= MAX_STACK_SIZE) {
      console.warn(
        `[ModalStackRegistry] Limite de ${MAX_STACK_SIZE} modais simultâneos atingido. ` +
        'Registro rejeitado.'
      )
      return
    }

    this._stack.push(modal)
  }

  /**
   * Remove um modal da stack.
   *
   * Se o modal não estiver registrado, a operação é ignorada silenciosamente.
   * Remove o modal independente de sua posição na stack.
   *
   * @param {object} modal - Instância do modal a ser removido
   * @returns {void}
   */
  unregister (modal) {
    if (!modal) return

    const index = this._stack.indexOf(modal)
    if (index === -1) return

    this._stack.splice(index, 1)
  }

  /**
   * Verifica se o modal informado é o topo da stack (último aberto).
   *
   * Utilizado pelo handler de popstate para garantir que apenas o modal
   * do topo responda ao evento de navegação para trás.
   *
   * @param {object} modal - Instância do modal a verificar
   * @returns {boolean} true se o modal é o topo da stack, false caso contrário
   */
  isTop (modal) {
    if (!modal || this._stack.length === 0) return false

    return this._stack[this._stack.length - 1] === modal
  }

  /**
   * Retorna todos os modais acima do modal especificado na stack.
   *
   * Utilizado para implementar cascata de fechamento: quando um modal
   * não-topo é fechado programaticamente, todos os modais acima dele
   * devem ser fechados na ordem LIFO (do topo para baixo).
   *
   * @param {object} modal - Instância do modal de referência
   * @returns {Array<object>} Array de modais acima do especificado (do mais alto para o mais baixo)
   */
  getAbove (modal) {
    if (!modal) return []

    const index = this._stack.indexOf(modal)
    if (index === -1) return []

    // Retorna modais acima na ordem do topo para baixo (reverso)
    return this._stack.slice(index + 1).reverse()
  }

  /**
   * Limpa toda a stack de modais.
   *
   * Utilizado principalmente em testes para garantir estado limpo
   * entre execuções. Não dispara eventos de fechamento nos modais.
   *
   * @returns {void}
   */
  clear () {
    this._stack = []
  }

  /**
   * Marca o evento popstate como consumido para o tick atual.
   *
   * Deve ser chamado pelo modal do topo ao responder a um popstate,
   * impedindo que outros listeners de popstate (modais inferiores na stack)
   * também respondam ao mesmo evento no mesmo tick.
   * O reset automático via queueMicrotask() garante que o próximo
   * popstate (ex: segundo goBack do usuário) será processado normalmente.
   *
   * @returns {void}
   */
  consumePopstate () {
    this._popstateConsumed = true
    queueMicrotask(() => { this._popstateConsumed = false })
  }

  /**
   * Verifica se o evento popstate já foi consumido no tick atual.
   *
   * Utilizado como guard no início do handler de popstate de cada modal.
   * Se retornar true, o handler deve retornar imediatamente sem fechar o modal,
   * pois outro modal (o topo) já respondeu ao mesmo evento.
   *
   * @returns {boolean} true se o popstate já foi consumido neste tick, false caso contrário
   */
  isPopstateConsumed () {
    return this._popstateConsumed
  }

  /**
   * Número de modais atualmente na stack.
   *
   * @type {number}
   * @readonly
   */
  get size () {
    return this._stack.length
  }
}

/**
 * Instância singleton do ModalStackRegistry.
 *
 * Exportada como instância única para garantir que todos os modais
 * do app compartilhem o mesmo registro de stack, independente de
 * onde são importados.
 *
 * @type {ModalStackRegistry}
 */
export const modalStackRegistry = new ModalStackRegistry()
